import {
  type FunctionComponent,
  type ReactNode,
  createContext,
  useContext,
  useMemo,
} from 'react';

import {
  Job,
  JobCompanyInfo,
  PaymentOption,
  bacsStripePromise,
} from '@kanda-libs/ks-frontend-services';
import { themeConfig } from '@kanda-libs/ks-tailwind-config';

import {
  Appearance,
  CssFontSource,
  CustomFontSource,
  Stripe,
} from '@stripe/stripe-js';
import { getLatestBankPaymentFromJob } from 'utils';

export interface BacsPaymentFormContextType {
  title?: string;
  companyName: string;
  showHeading: boolean;
  total?: number;
  deposit?: number;
  isLoading: boolean;
  jobId?: string;
  paymentOption?: PaymentOption;
  clientSecret?: string;
  stripeAppearance?: Appearance;
  stripeFont?: Array<CssFontSource | CustomFontSource>;
  stripePromise: PromiseLike<Stripe | null> | Stripe | null;
  customHeading: ReactNode;
}

export const BacsPaymentFormContext =
  createContext<BacsPaymentFormContextType | null>(null);

export const useBacsPaymentFormContext = () =>
  useContext(BacsPaymentFormContext) as BacsPaymentFormContextType;

export interface BacsPaymentFormProviderProps {
  job?: Job;
  jobCompanyInfo?: JobCompanyInfo;
  title?: string;
  showHeading?: boolean;
  customHeading?: ReactNode;
  children: ReactNode;
}

const BacsPaymentFormProvider: FunctionComponent<BacsPaymentFormProviderProps> =
  function ({
    job,
    jobCompanyInfo,
    title,
    showHeading = false,
    customHeading = null,
    children,
  }) {
    const isLoading = useMemo(() => !job, [job]);

    const payment = useMemo(() => getLatestBankPaymentFromJob(job), [job]);

    const jobId = useMemo(() => job?.id, [job]);
    const paymentOption = useMemo(() => payment?.payment_option, [payment]);
    const clientSecret = useMemo(() => payment?.xref, [payment]);

    const { theme } = themeConfig;
    const fontFamilyRaw = theme?.extend?.fontFamily?.sans?.[0];
    const fontFamily = `${fontFamilyRaw?.replace(/"/g, '')}, sans-serif`;

    const stripeAppearance: Appearance | undefined = useMemo(() => {
      if (!fontFamily) return undefined;
      return {
        theme: 'stripe' as Appearance['theme'],
        variables: {
          fontFamily,
          spacingUnit: theme.spacing[1],
          colorBackground: theme.colors['neutral-100'],
          colorText: theme.colors['neutral-700'],
          marginBottom: '16px',
          spacingGridRow: '16px',
          borderRadius: '8px',
          borderColor: theme.colors['neutral-700'],
        },
        rules: {
          '.Label': {
            fontWeight: 'bold',
            marginBottom: theme.spacing[3],
            fontSize: '12px',
          },
        },
      };
    }, [fontFamily, theme]);

    const stripeFont = useMemo(() => {
      if (!fontFamilyRaw) return undefined;
      return [
        {
          family: fontFamilyRaw,
          src: 'url(https://storage.googleapis.com/kanda-fonts/GalanoGrotesqueRegular.ttf)',
          weight: '400',
        },
        {
          family: fontFamilyRaw,
          src: 'url(https://storage.googleapis.com/kanda-fonts/GalanoGrotesqueBold.ttf)',
          weight: '600',
        },
      ];
    }, [fontFamilyRaw]);

    const stripePromise = useMemo(() => {
      if (!job) return null;
      return bacsStripePromise;
    }, [job]);

    const companyName = useMemo(
      () => jobCompanyInfo?.company_name ?? 'your tradesperson',
      [jobCompanyInfo],
    );

    const total = useMemo(
      () => job?.total?.amount_vat_inclusive?.amount,
      [job],
    );

    const deposit = useMemo(() => job?.deposit_value?.amount, [job]);

    const values: BacsPaymentFormContextType = useMemo(
      () => ({
        title,
        companyName,
        total,
        deposit,
        showHeading,
        isLoading,
        jobId,
        paymentOption,
        clientSecret,
        stripeAppearance,
        stripeFont,
        stripePromise,
        customHeading,
      }),
      [
        title,
        companyName,
        total,
        deposit,
        showHeading,
        isLoading,
        jobId,
        paymentOption,
        clientSecret,
        stripeAppearance,
        stripeFont,
        stripePromise,
        customHeading,
      ],
    );

    return (
      <BacsPaymentFormContext.Provider value={values}>
        {children}
      </BacsPaymentFormContext.Provider>
    );
  };

export default BacsPaymentFormProvider;
